import { useEffect, useRef, useState, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ENVIRONMENT_TYPES,
  LINE_STYLES,
  CONNECTOR_ROUTINGS,
  LABEL_POSITIONS,
  CABLE_TYPES,
  DEVICE_KINDS,
  FIBER_MODES,
  IP_MODES,
  PORT_COUNTS,
  PORT_PROTOCOLS,
  PORT_SPEEDS,
  SFP_COUNTS,
  SFP_MODULES,
  SWITCH_LAYERS,
  SWITCH_ROLES,
  buildPorts,
  connectorLineStyle,
  deviceFields,
  isValidIpv4,
  isValidPortNumber,
  labelAlignment,
  supportsLabelPosition,
  layerFields,
  recommendedSwitchWidth,
  recommendedHostSize,
  userLabel,
  BRIDGE_MODES,
  FIREWALL_ROLES,
  IFACE_ZONES,
  RULE_ACTIONS,
  RULE_PROTOCOLS,
  VPN_KINDS,
  VPN_PROTOCOLS,
  HA_ROLES,
  recommendedFirewallWidth,
  type FirewallData,
  type FirewallInterface,
  type FirewallRule,
  type NatRule,
  type VpnDef,
  type RuleAction,
  type RuleProtocol,
  type VpnKind,
  type VpnProtocol,
  type IfaceZone,
  type HaRole,
  type FirewallLegendData,
  type UserDef,
  type BridgeMode,
  type CableType,
  type Connector,
  type DeviceData,
  type DeviceKind,
  type HostData,
  type VmDef,
  type EnvironmentType,
  type FiberMode,
  type IpMode,
  type PortDef,
  type PortProtocol,
  type PortSpeed,
  type ServicePort,
  type SwitchData,
  type SwitchLayer,
  type SwitchRole,
  type LineStyle,
  type ConnectorRouting,
  type LabelPosition,
  type SceneNode,
  type SwitchLegendData,
  type VlanDef,
  CLUSTER_STORAGE_KINDS,
  VOLUME_REDUNDANCY,
  nextVolumeColor,
  recommendedClusterStorageSize,
  sceneVolumes,
  type ClusterStorageData,
  type VolumeDef,
  type VolumeRef,
} from '@system-atlas/shared';
import { Button, Label, Select, TextInput, Textarea } from '../components/ui';
import { cn } from '../lib/utils';
import { newId } from './nodeFactory';

const FONT_SIZES = [10, 12, 14, 16, 20, 24, 32, 48];
import { ENV_COLORS } from './colors';
import type { CanvasFormat } from './canvasFormats';

type ArrowMode = 'none' | 'end' | 'start' | 'both';
const ARROW_MODES: ArrowMode[] = ['end', 'both', 'start', 'none'];
const STROKE_WIDTHS = [1, 2, 3, 4, 6, 8];

function arrowModeOf(c: Connector): ArrowMode {
  const start = c.arrowStart ?? false;
  const end = c.arrowEnd ?? true;
  return start && end ? 'both' : start ? 'start' : end ? 'end' : 'none';
}

function arrowModePatch(mode: ArrowMode): Partial<Connector> {
  return {
    arrowStart: mode === 'start' || mode === 'both',
    arrowEnd: mode === 'end' || mode === 'both',
  };
}

/** Verschiebt ein Array-Element um dir (-1/+1); ausserhalb der Grenzen unveraendert. */
function moveItem<T>(arr: T[], i: number, dir: number): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

const CANVAS_FORMATS: CanvasFormat[] = ['a4-l', 'a4-p', 'a3-l', 'a3-p', 'free'];

/** Effektive Position fürs 3×3-Raster (berücksichtigt das Alt-Feld align). */
function effectiveLabelPosition(node: SceneNode): LabelPosition {
  const { align, verticalAlign } = labelAlignment(node);
  return `${verticalAlign}-${align}` as LabelPosition;
}

/**
 * Persistiert Freitext erst nach kurzer Tipp-Pause (Default 300 ms) bzw. beim Verlassen
 * des Feldes – damit nicht jeder Tastendruck einen eigenen Undo-Schritt erzeugt.
 * Hält den Wert lokal und übernimmt externe Änderungen nur, solange das Feld nicht fokussiert ist.
 */
function useDebouncedCommit(value: string, onCommit: (v: string) => void, delay: number) {
  const [local, setLocal] = useState(value);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!focused.current) setLocal(value);
  }, [value]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  const handleChange = (v: string) => {
    setLocal(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(v), delay);
  };
  const handleFocus = () => {
    focused.current = true;
  };
  const handleBlur = () => {
    focused.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (local !== value) onCommit(local);
  };
  return { local, handleChange, handleFocus, handleBlur };
}

function DebouncedTextInput({
  value,
  onCommit,
  delay = 300,
  ...rest
}: { value: string; onCommit: (v: string) => void; delay?: number } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>) {
  const { local, handleChange, handleFocus, handleBlur } = useDebouncedCommit(value, onCommit, delay);
  return (
    <TextInput
      {...rest}
      value={local}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => handleChange(e.target.value)}
    />
  );
}

function DebouncedTextarea({
  value,
  onCommit,
  delay = 300,
  ...rest
}: { value: string; onCommit: (v: string) => void; delay?: number } & Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
>) {
  const { local, handleChange, handleFocus, handleBlur } = useDebouncedCommit(value, onCommit, delay);
  return (
    <Textarea
      {...rest}
      value={local}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => handleChange(e.target.value)}
    />
  );
}

function LabelPositionGrid({
  value,
  onChange,
}: {
  value: LabelPosition;
  onChange: (position: LabelPosition) => void;
}) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t('editor.labelPosition')} className="grid w-fit grid-cols-3 gap-1">
      {LABEL_POSITIONS.map((position) => (
        <button
          key={position}
          type="button"
          role="radio"
          aria-checked={value === position}
          aria-label={t(`editor.labelPos.${position}`)}
          title={t(`editor.labelPos.${position}`)}
          onClick={() => onChange(position)}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded border text-xs',
            value === position
              ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
              : 'border-slate-300 bg-white text-slate-400 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700',
          )}
        >
          ●
        </button>
      ))}
    </div>
  );
}

function ServicePortsField({
  ports,
  onChange,
}: {
  ports: ServicePort[];
  onChange: (next: ServicePort[]) => void;
}) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<ServicePort>) =>
    onChange(ports.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const add = () => onChange([...ports, { port: 0, protocol: 'tcp' }]);
  const remove = (i: number) => onChange(ports.filter((_, idx) => idx !== i));

  return (
    <div>
      <Label>{t('editor.device.servicePorts')}</Label>
      <div className="space-y-2">
        {ports.map((p, i) => {
          const invalid = !isValidPortNumber(p.port);
          return (
            <div key={i} className="space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700">
              {/* Zeile 1: Port + Protokoll */}
              <div className="flex items-center gap-1">
                <TextInput
                  type="number"
                  className={cn('w-20', invalid && 'border-red-500 focus:border-red-500 focus:ring-red-400/40')}
                  value={p.port || ''}
                  placeholder={t('editor.device.servicePortNumberPlaceholder')}
                  aria-label={t('editor.device.servicePortNumber')}
                  onChange={(e) =>
                    update(i, { port: Math.max(0, Math.min(65535, Math.trunc(Number(e.target.value)))) })
                  }
                />
                <Select
                  className="flex-1"
                  value={p.protocol ?? 'tcp'}
                  aria-label={t('editor.device.servicePortProtocol')}
                  onChange={(e) => update(i, { protocol: e.target.value as PortProtocol })}
                >
                  {PORT_PROTOCOLS.map((proto) => (
                    <option key={proto} value={proto}>
                      {t(`editor.device.protocolOption.${proto}`)}
                    </option>
                  ))}
                </Select>
              </div>
              {/* Zeile 2: Beschreibung + Loeschen */}
              <div className="flex items-center gap-1">
                <TextInput
                  className="flex-1"
                  value={p.service ?? ''}
                  placeholder={t('editor.device.servicePortServicePlaceholder')}
                  aria-label={t('editor.device.servicePortService')}
                  onChange={(e) => update(i, { service: e.target.value })}
                />
                <Button
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  title={t('editor.device.removeServicePort')}
                  onClick={() => remove(i)}
                >
                  ✕
                </Button>
              </div>
            </div>
          );
        })}
        <Button variant="secondary" className="w-full px-2 py-1 text-xs" onClick={add}>
          {t('editor.device.addServicePort')}
        </Button>
      </div>
    </div>
  );
}

function DeviceFields({
  device,
  vlans,
  users,
  onChange,
  onPickImage,
}: {
  device: DeviceData;
  vlans: VlanDef[];
  users: UserDef[];
  onChange: (next: DeviceData) => void;
  onPickImage: () => void;
}) {
  const { t } = useTranslation();
  const fields = deviceFields(device.kind);
  const set = (patch: Partial<DeviceData>) => onChange({ ...device, ...patch });
  const showIp = fields.ip && (device.ipMode === 'static' || device.ipMode === 'dhcp-fixed');
  const ipInvalid = (device.ipValue ?? '') !== '' && !isValidIpv4(device.ipValue ?? '');

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="d-kind">{t('editor.device.type')}</Label>
        <Select
          id="d-kind"
          className="w-full"
          value={device.kind}
          onChange={(e) => set({ kind: e.target.value as DeviceKind })}
        >
          {DEVICE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`editor.device.kind.${k}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label>{t('editor.device.icon')}</Label>
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1 px-2 py-1 text-xs" onClick={onPickImage}>
            {device.customImageId ? t('editor.device.changeImage') : t('editor.device.chooseImage')}
          </Button>
          {device.customImageId && (
            <Button
              variant="secondary"
              className="px-2 py-1 text-xs"
              onClick={() => set({ customImageId: undefined })}
            >
              {t('editor.device.resetIcon')}
            </Button>
          )}
        </div>
      </div>
      <div>
        <Label htmlFor="d-vlan">{t('editor.device.vlan')}</Label>
        <Select
          id="d-vlan"
          className="w-full"
          value={device.vlanRef ?? ''}
          onChange={(e) => set({ vlanRef: e.target.value === '' ? undefined : e.target.value })}
        >
          <option value="">{t('editor.device.vlanNone')}</option>
          {vlans.map((v) => (
            <option key={v.id} value={v.id}>
              {`${v.vlanId} · ${v.name}`}
            </option>
          ))}
        </Select>
      </div>
      {fields.ip && (
        <div>
          <Label htmlFor="d-ipmode">{t('editor.device.ipMode')}</Label>
          <Select
            id="d-ipmode"
            className="w-full"
            value={device.ipMode ?? ''}
            onChange={(e) => set({ ipMode: e.target.value === '' ? undefined : (e.target.value as IpMode) })}
          >
            <option value="">{t('editor.device.ipModeNone')}</option>
            {IP_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`editor.device.ipModeOption.${m}`)}
              </option>
            ))}
          </Select>
        </div>
      )}
      {showIp && (
        <div>
          <Label htmlFor="d-ip">{t('editor.device.ip')}</Label>
          <DebouncedTextInput
            id="d-ip"
            className={cn('font-mono', ipInvalid && 'border-red-500 focus:border-red-500 focus:ring-red-400/40')}
            value={device.ipValue ?? ''}
            placeholder={t('editor.device.ipPlaceholder')}
            onCommit={(v) => set({ ipValue: v })}
          />
          {ipInvalid && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t('editor.device.invalidIp')}</p>
          )}
        </div>
      )}
      <div>
        <Label htmlFor="d-dns">{t('editor.device.dns')}</Label>
        <DebouncedTextInput
          id="d-dns"
          value={device.dns ?? ''}
          placeholder={t('editor.device.dnsPlaceholder')}
          onCommit={(v) => set({ dns: v })}
        />
      </div>
      {fields.user && (
        <div className="space-y-1">
          <Label htmlFor="d-user">{t('editor.device.user')}</Label>
          {users.length > 0 && (
            <Select
              id="d-userref"
              className="w-full"
              value={device.userRef ?? ''}
              onChange={(e) => set({ userRef: e.target.value || undefined })}
            >
              <option value="">{t('editor.device.userNone')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {userLabel(u)}
                  {u.enabled === false ? ` (${t('editor.device.userDisabled')})` : ''}
                </option>
              ))}
            </Select>
          )}
          <DebouncedTextInput
            id="d-user"
            value={device.user ?? ''}
            placeholder={t('editor.device.userManual')}
            onCommit={(v) => set({ user: v })}
          />
        </div>
      )}
      <ServicePortsField
        ports={device.servicePorts ?? []}
        onChange={(servicePorts) => set({ servicePorts: servicePorts.length ? servicePorts : undefined })}
      />
    </div>
  );
}

function SwitchFields({
  node,
  onChangeNode,
}: {
  node: SceneNode;
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  const sw = node.switch!;
  const apply = (patch: Partial<SwitchData>) => {
    const next: SwitchData = { ...sw, ...patch };
    const ports = buildPorts(next);
    const width =
      patch.portCount !== undefined || patch.sfpCount !== undefined
        ? recommendedSwitchWidth(next.portCount, next.sfpCount)
        : node.width;
    onChangeNode({ switch: { ...next, ports }, width });
  };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="sw-ports">{t('editor.switch.portCount')}</Label>
        <Select
          id="sw-ports"
          className="w-full"
          value={sw.portCount}
          onChange={(e) => apply({ portCount: Number(e.target.value) as SwitchData['portCount'] })}
        >
          {PORT_COUNTS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="sw-sfp">{t('editor.switch.sfpCount')}</Label>
        <Select
          id="sw-sfp"
          className="w-full"
          value={sw.sfpCount ?? 0}
          onChange={(e) => apply({ sfpCount: Number(e.target.value) as SwitchData['sfpCount'] })}
        >
          {SFP_COUNTS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="sw-layer">{t('editor.switch.layer')}</Label>
        <Select
          id="sw-layer"
          className="w-full"
          value={sw.layer}
          onChange={(e) => apply({ layer: Number(e.target.value) as SwitchLayer })}
        >
          {SWITCH_LAYERS.map((l) => (
            <option key={l} value={l}>
              {`L${l}`}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="sw-role">{t('editor.switch.role')}</Label>
        <Select
          id="sw-role"
          className="w-full"
          value={sw.role}
          onChange={(e) => apply({ role: e.target.value as SwitchRole })}
        >
          {SWITCH_ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`editor.switch.roleOption.${r}`)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

function PortFields({
  node,
  portIndex,
  vlans,
  onChange,
}: {
  node: SceneNode;
  portIndex: number;
  vlans: VlanDef[];
  onChange: (patch: Partial<PortDef>) => void;
}) {
  const { t } = useTranslation();
  const sw = node.switch!;
  const port = sw.ports.find((p) => p.index === portIndex);
  if (!port) return null;
  const fields = layerFields(sw.layer);
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <p className="text-sm font-medium">
        {t('editor.switch.portTitle', { index: portIndex })}
        <span className="ml-2 text-xs text-slate-500">{port.portType === 'sfp' ? 'SFP' : 'RJ45'}</span>
      </p>
      {fields.vlan && (
        <div>
          <Label htmlFor="pt-vlan">{t('editor.device.vlan')}</Label>
          <Select
            id="pt-vlan"
            className="w-full"
            value={port.vlanRef ?? ''}
            onChange={(e) => onChange({ vlanRef: e.target.value || undefined })}
          >
            <option value="">{t('editor.device.vlanNone')}</option>
            {vlans.map((v) => (
              <option key={v.id} value={v.id}>
                {`${v.vlanId} · ${v.name}`}
              </option>
            ))}
          </Select>
        </div>
      )}
      {fields.vlan && (
        <div>
          <Label>{t('editor.switch.taggedVlans')}</Label>
          <div className="space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700">
            {vlans.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('editor.switch.taggedVlansEmpty')}</p>
            ) : (
              vlans.map((v) => {
                const checked = (port.taggedVlanRefs ?? []).includes(v.id);
                return (
                  <label key={v.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const cur = port.taggedVlanRefs ?? [];
                        const next = e.target.checked ? [...cur, v.id] : cur.filter((id) => id !== v.id);
                        onChange({ taggedVlanRefs: next.length ? next : undefined });
                      }}
                    />
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: v.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{`${v.vlanId} · ${v.name}`}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
      {fields.speed && (
        <div>
          <Label htmlFor="pt-speed">{t('editor.switch.speed')}</Label>
          <Select
            id="pt-speed"
            className="w-full"
            value={port.speed ?? ''}
            onChange={(e) => onChange({ speed: (e.target.value || undefined) as PortSpeed | undefined })}
          >
            <option value="">{t('editor.switch.speedNone')}</option>
            {PORT_SPEEDS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </div>
      )}
      {port.portType === 'sfp' && (
        <div>
          <Label htmlFor="pt-sfp">{t('editor.switch.sfpModule')}</Label>
          <TextInput
            id="pt-sfp"
            list="pt-sfp-options"
            value={port.sfpModule ?? ''}
            placeholder={t('editor.switch.sfpModulePlaceholder')}
            onChange={(e) => onChange({ sfpModule: e.target.value || undefined })}
          />
          <datalist id="pt-sfp-options">
            {SFP_MODULES.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>
      )}
      {fields.ip && (
        <div>
          <Label htmlFor="pt-ip">{t('editor.device.ip')}</Label>
          <TextInput id="pt-ip" className="font-mono" value={port.ip ?? ''} onChange={(e) => onChange({ ip: e.target.value })} />
        </div>
      )}
      {fields.mac && (
        <div>
          <Label htmlFor="pt-mac">{t('editor.switch.mac')}</Label>
          <TextInput id="pt-mac" className="font-mono" value={port.mac ?? ''} onChange={(e) => onChange({ mac: e.target.value })} />
        </div>
      )}
      {fields.dose && (
        <div>
          <Label htmlFor="pt-dose">{t('editor.switch.dose')}</Label>
          <TextInput id="pt-dose" value={port.dose ?? ''} onChange={(e) => onChange({ dose: e.target.value })} />
        </div>
      )}
      {fields.service && (
        <div>
          <Label htmlFor="pt-service">{t('editor.switch.service')}</Label>
          <TextInput id="pt-service" value={port.service ?? ''} onChange={(e) => onChange({ service: e.target.value })} />
        </div>
      )}
      {fields.uplink && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={port.uplink ?? false}
            onChange={(e) => onChange({ uplink: e.target.checked })}
          />
          {t('editor.switch.uplink')}
        </label>
      )}
    </div>
  );
}

/** Host/VM-Eigenschaften (Baustein E): OS + Hypervisor + CRUD der VM-Kacheln. */
function HostFields({
  node,
  vlans,
  volumes,
  onChangeNode,
}: {
  node: SceneNode;
  vlans: VlanDef[];
  volumes: VolumeRef[];
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  // Akkordeon-Zustand der VM-Kacheln (Kopf klickbar, Details bei Bedarf).
  const [openVms, setOpenVms] = useState<Set<string>>(new Set());
  const toggleVm = (id: string) =>
    setOpenVms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const host = node.host;
  if (!host) return null;

  const setHost = (patch: Partial<HostData>) => onChangeNode({ host: { ...host, ...patch } });
  // VM-Anzahl aendert sich -> Box waechst mit (analog Switch-Portzahl).
  const setVms = (vms: VmDef[], resize = false) => {
    if (resize) {
      const size = recommendedHostSize(vms.length);
      onChangeNode({ host: { ...host, vms }, width: size.width, height: size.height });
    } else {
      onChangeNode({ host: { ...host, vms } });
    }
  };
  const updateVm = (i: number, patch: Partial<VmDef>) =>
    setVms(host.vms.map((vm, idx) => (idx === i ? { ...vm, ...patch } : vm)));
  const addVm = () => {
    const vm: VmDef = { id: newId(), name: '', active: true };
    setVms([...host.vms, vm], true);
    setOpenVms((prev) => new Set(prev).add(vm.id)); // neue VM gleich aufgeklappt zeigen
  };
  const removeVm = (i: number) => setVms(host.vms.filter((_, idx) => idx !== i), true);

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="h-os">{t('editor.host.os')}</Label>
        <DebouncedTextInput id="h-os" value={host.os ?? ''} onCommit={(v) => setHost({ os: v })} />
      </div>
      <div>
        <Label htmlFor="h-hyp">{t('editor.host.hypervisor')}</Label>
        <DebouncedTextInput
          id="h-hyp"
          value={host.hypervisor ?? ''}
          onCommit={(v) => setHost({ hypervisor: v })}
        />
      </div>
      <div>
        <Label htmlFor="h-ip">{t('editor.host.ip')}</Label>
        <DebouncedTextInput id="h-ip" value={host.ip ?? ''} onCommit={(v) => setHost({ ip: v })} />
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          <Label htmlFor="h-cores">{t('editor.host.cores')}</Label>
          <TextInput
            id="h-cores"
            type="number"
            value={host.cores ?? ''}
            onChange={(e) =>
              setHost({ cores: e.target.value === '' ? undefined : Math.max(0, Math.trunc(Number(e.target.value))) })
            }
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="h-ram">{t('editor.host.ram')}</Label>
          <TextInput
            id="h-ram"
            type="number"
            value={host.ram ?? ''}
            onChange={(e) => setHost({ ram: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
          />
        </div>
      </div>
      <div>
        <Label>{t('editor.host.vms')}</Label>
        <div className="space-y-2">
          {host.vms.map((vm, i) => {
            const open = openVms.has(vm.id);
            return (
              <div key={vm.id} className="rounded-md border border-slate-200 dark:border-slate-700">
                {/* Akkordeon-Kopf: Name + Aktiv-Status, klickbar */}
                <div className="flex items-center gap-1 p-1">
                  <button
                    type="button"
                    onClick={() => toggleVm(vm.id)}
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-left text-sm hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-slate-700"
                  >
                    <span aria-hidden="true" className="text-slate-400">{open ? '▾' : '▸'}</span>
                    <span className="truncate font-medium">{vm.name || t('editor.host.vmName')}</span>
                    <span
                      className={cn(
                        'ml-auto h-2 w-2 shrink-0 rounded-full',
                        vm.active ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600',
                      )}
                      title={vm.active ? t('editor.host.active') : t('editor.host.inactive')}
                    />
                  </button>
                  <Button
                    variant="secondary"
                    className="px-2 py-1 text-xs"
                    title={t('editor.host.removeVm')}
                    aria-label={t('editor.host.removeVm')}
                    onClick={() => removeVm(i)}
                  >
                    ✕
                  </Button>
                </div>
                {open && (
                  <div className="space-y-1 border-t border-slate-200 p-2 dark:border-slate-700">
                    <DebouncedTextInput
                      value={vm.name}
                      placeholder={t('editor.host.vmName')}
                      aria-label={t('editor.host.vmName')}
                      onCommit={(v) => updateVm(i, { name: v })}
                    />
                    <DebouncedTextInput
                      value={vm.os ?? ''}
                      placeholder={t('editor.host.vmOs')}
                      aria-label={t('editor.host.vmOs')}
                      onCommit={(v) => updateVm(i, { os: v })}
                    />
                    <DebouncedTextInput
                      value={vm.apps ?? ''}
                      placeholder={t('editor.host.vmApps')}
                      aria-label={t('editor.host.vmApps')}
                      onCommit={(v) => updateVm(i, { apps: v })}
                    />
                    <div className="flex items-center gap-1">
                      <DebouncedTextInput
                        className="flex-1"
                        value={vm.ports ?? ''}
                        placeholder={t('editor.host.vmPorts')}
                        aria-label={t('editor.host.vmPorts')}
                        onCommit={(v) => updateVm(i, { ports: v })}
                      />
                      <DebouncedTextInput
                        className="flex-1 font-mono"
                        value={vm.ip ?? ''}
                        placeholder={t('editor.host.vmIp')}
                        aria-label={t('editor.host.vmIp')}
                        onCommit={(v) => updateVm(i, { ip: v })}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <TextInput
                        className="flex-1"
                        type="number"
                        value={vm.cores ?? ''}
                        placeholder={t('editor.host.vmCores')}
                        aria-label={t('editor.host.vmCores')}
                        onChange={(e) =>
                          updateVm(i, { cores: e.target.value === '' ? undefined : Math.max(0, Math.trunc(Number(e.target.value))) })
                        }
                      />
                      <TextInput
                        className="flex-1"
                        type="number"
                        value={vm.ram ?? ''}
                        placeholder={t('editor.host.vmRam')}
                        aria-label={t('editor.host.vmRam')}
                        onChange={(e) =>
                          updateVm(i, { ram: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })
                        }
                      />
                    </div>
                    <Select
                      className="w-full"
                      value={vm.vlanRef ?? ''}
                      aria-label={t('editor.host.vmVlan')}
                      onChange={(e) => updateVm(i, { vlanRef: e.target.value === '' ? undefined : e.target.value })}
                    >
                      <option value="">{t('editor.device.vlanNone')}</option>
                      {vlans.map((v) => (
                        <option key={v.id} value={v.id}>
                          {`${v.vlanId} · ${v.name}`}
                        </option>
                      ))}
                    </Select>
                    <Select
                      className="w-full"
                      value={vm.volumeRef ?? ''}
                      aria-label={t('editor.clusterStorage.vmVolume')}
                      onChange={(e) => updateVm(i, { volumeRef: e.target.value === '' ? undefined : e.target.value })}
                    >
                      <option value="">{t('editor.clusterStorage.vmVolumeNone')}</option>
                      {volumes.map((vr) => (
                        <option key={vr.volume.id} value={vr.volume.id}>
                          {vr.storageLabel ? `${vr.volume.name} (${vr.storageLabel})` : vr.volume.name}
                        </option>
                      ))}
                    </Select>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={vm.active}
                        onChange={(e) => updateVm(i, { active: e.target.checked })}
                      />
                      {t('editor.host.vmActive')}
                    </label>
                  </div>
                )}
              </div>
            );
          })}
          <Button variant="secondary" className="w-full px-2 py-1 text-xs" onClick={addVm}>
            {t('editor.host.addVm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Clusterstorage-Eigenschaften (Baustein H): Cluster/Typ + CRUD fuer Volumes. */
function ClusterStorageFields({
  node,
  onChangeNode,
}: {
  node: SceneNode;
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  const data = node.clusterStorage;
  if (!data) return null;
  const set = (patch: Partial<ClusterStorageData>) => onChangeNode({ clusterStorage: { ...data, ...patch } });
  const setVolumes = (volumes: VolumeDef[], resize = false) => {
    if (resize) {
      const size = recommendedClusterStorageSize(volumes.length);
      onChangeNode({ clusterStorage: { ...data, volumes }, width: size.width, height: size.height });
    } else {
      onChangeNode({ clusterStorage: { ...data, volumes } });
    }
  };
  const updateVol = (i: number, patch: Partial<VolumeDef>) =>
    setVolumes(data.volumes.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const addVol = () =>
    setVolumes([...data.volumes, { id: newId(), name: '', color: nextVolumeColor(data.volumes) }], true);
  const removeVol = (i: number) => setVolumes(data.volumes.filter((_, idx) => idx !== i), true);

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="cs-cluster">{t('editor.clusterStorage.cluster')}</Label>
        <DebouncedTextInput id="cs-cluster" value={data.cluster ?? ''} onCommit={(v) => set({ cluster: v })} />
      </div>
      <div>
        <Label htmlFor="cs-kind">{t('editor.clusterStorage.kind')}</Label>
        <Select id="cs-kind" className="w-full" value={data.kind ?? ''} onChange={(e) => set({ kind: e.target.value === '' ? undefined : (e.target.value as ClusterStorageData['kind']) })}>
          <option value="">—</option>
          {CLUSTER_STORAGE_KINDS.map((k) => (
            <option key={k} value={k}>{t(`editor.clusterStorage.kindOption.${k}`)}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label>{t('editor.clusterStorage.volumes')}</Label>
        <div className="space-y-2">
          {data.volumes.map((vol, i) => (
            <div key={vol.id} className="space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700">
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  className="h-8 w-10 shrink-0 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                  value={vol.color}
                  aria-label={t('editor.clusterStorage.volColor')}
                  onChange={(e) => updateVol(i, { color: e.target.value })}
                />
                <DebouncedTextInput
                  className="flex-1"
                  value={vol.name}
                  placeholder={t('editor.clusterStorage.volName')}
                  aria-label={t('editor.clusterStorage.volName')}
                  onCommit={(v) => updateVol(i, { name: v })}
                />
                <Button variant="secondary" className="px-2 py-1 text-xs" title={t('editor.clusterStorage.removeVolume')} aria-label={t('editor.clusterStorage.removeVolume')} onClick={() => removeVol(i)}>✕</Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <DebouncedTextInput className="w-full min-w-0" value={vol.fs ?? ''} placeholder={t('editor.clusterStorage.volFs')} aria-label={t('editor.clusterStorage.volFs')} onCommit={(v) => updateVol(i, { fs: v })} />
                <Select className="w-full min-w-0" value={vol.redundancy ?? ''} aria-label={t('editor.clusterStorage.volRedundancy')} onChange={(e) => updateVol(i, { redundancy: e.target.value === '' ? undefined : (e.target.value as VolumeDef['redundancy']) })}>
                  <option value="">{t('editor.clusterStorage.volRedundancy')}</option>
                  {VOLUME_REDUNDANCY.map((rk) => (
                    <option key={rk} value={rk}>{t(`editor.clusterStorage.volRedundancyOption.${rk}`)}</option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <TextInput className="w-full min-w-0" type="number" value={vol.sizeGb ?? ''} placeholder={t('editor.clusterStorage.volSize')} aria-label={t('editor.clusterStorage.volSize')} onChange={(e) => updateVol(i, { sizeGb: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })} />
                <TextInput className="w-full min-w-0" type="number" value={vol.usedGb ?? ''} placeholder={t('editor.clusterStorage.volUsed')} aria-label={t('editor.clusterStorage.volUsed')} onChange={(e) => updateVol(i, { usedGb: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })} />
              </div>
              <div className="grid grid-cols-2 items-center gap-2">
                <TextInput className="w-full min-w-0" type="number" value={vol.warnAtPercent ?? ''} placeholder={t('editor.clusterStorage.volWarnAt')} aria-label={t('editor.clusterStorage.volWarnAt')} onChange={(e) => updateVol(i, { warnAtPercent: e.target.value === '' ? undefined : Math.min(100, Math.max(0, Math.trunc(Number(e.target.value)))) })} />
                <label className="flex min-w-0 items-center gap-1 text-sm">
                  <input type="checkbox" className="shrink-0" checked={vol.warnOff ?? false} onChange={(e) => updateVol(i, { warnOff: e.target.checked || undefined })} />
                  <span className="min-w-0 break-words">{t('editor.clusterStorage.volWarnOff')}</span>
                </label>
              </div>
              <DebouncedTextInput value={vol.note ?? ''} placeholder={t('editor.clusterStorage.volNote')} aria-label={t('editor.clusterStorage.volNote')} onCommit={(v) => updateVol(i, { note: v })} />
            </div>
          ))}
          <Button variant="secondary" className="w-full px-2 py-1 text-xs" onClick={addVol}>
            {t('editor.clusterStorage.addVolume')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Firewall/Router-Eigenschaften (Baustein F): Rolle/Modell/HA + CRUD fuer Interfaces, Regeln, NAT, VPNs. */
function FirewallFields({
  node,
  vlans,
  firewallNodes,
  onChangeNode,
}: {
  node: SceneNode;
  vlans: VlanDef[];
  firewallNodes: { id: string; label: string }[];
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  const fw = node.firewall!;
  const set = (patch: Partial<FirewallData>) => onChangeNode({ firewall: { ...fw, ...patch } });
  // Interfaces: Reihenfolge = Andock-Index -> nicht sortierbar; Box waechst mit der Anzahl.
  const setIfaces = (interfaces: FirewallInterface[]) =>
    onChangeNode({ firewall: { ...fw, interfaces }, width: recommendedFirewallWidth(interfaces.length) });
  const addIface = () =>
    setIfaces([...fw.interfaces, { id: newId(), name: `IF${fw.interfaces.length + 1}`, zone: 'lan' }]);
  const updateIface = (i: number, patch: Partial<FirewallInterface>) =>
    setIfaces(fw.interfaces.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeIface = (i: number) => setIfaces(fw.interfaces.filter((_, idx) => idx !== i));
  // Regeln (geordnet).
  const setRules = (rules: FirewallRule[]) => set({ rules });
  const addRule = () => setRules([...fw.rules, { id: newId(), action: 'allow' }]);
  const updateRule = (i: number, patch: Partial<FirewallRule>) =>
    setRules(fw.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRule = (i: number) => setRules(fw.rules.filter((_, idx) => idx !== i));
  const moveRule = (i: number, dir: number) => setRules(moveItem(fw.rules, i, dir));
  // NAT (geordnet).
  const setNat = (nat: NatRule[]) => set({ nat });
  const addNat = () => setNat([...fw.nat, { id: newId() }]);
  const updateNat = (i: number, patch: Partial<NatRule>) =>
    setNat(fw.nat.map((n, idx) => (idx === i ? { ...n, ...patch } : n)));
  const removeNat = (i: number) => setNat(fw.nat.filter((_, idx) => idx !== i));
  const moveNat = (i: number, dir: number) => setNat(moveItem(fw.nat, i, dir));
  // VPNs (ungeordnet).
  const setVpns = (vpns: VpnDef[]) => set({ vpns });
  const addVpn = () => setVpns([...fw.vpns, { id: newId(), name: '', kind: 'site-to-site' }]);
  const updateVpn = (i: number, patch: Partial<VpnDef>) =>
    setVpns(fw.vpns.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const removeVpn = (i: number) => setVpns(fw.vpns.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="fw-role">{t('editor.firewall.role')}</Label>
        <Select id="fw-role" className="w-full" value={fw.role} onChange={(e) => set({ role: e.target.value as FirewallData['role'] })}>
          {FIREWALL_ROLES.map((r) => (<option key={r} value={r}>{t(`editor.firewall.roleOption.${r}`)}</option>))}
        </Select>
      </div>
      <div>
        <Label htmlFor="fw-model">{t('editor.firewall.model')}</Label>
        <DebouncedTextInput id="fw-model" value={fw.model ?? ''} onCommit={(v) => set({ model: v || undefined })} />
      </div>
      <div>
        <Label htmlFor="fw-ha">{t('editor.firewall.haRole')}</Label>
        <Select id="fw-ha" className="w-full" value={fw.haRole ?? 'standalone'} onChange={(e) => set({ haRole: e.target.value as HaRole })}>
          {HA_ROLES.map((r) => (<option key={r} value={r}>{t(`editor.firewall.haRoleOption.${r}`)}</option>))}
        </Select>
      </div>
      {fw.haRole && fw.haRole !== 'standalone' && (
        <div>
          <Label htmlFor="fw-hapeer">{t('editor.firewall.haPeer')}</Label>
          <Select id="fw-hapeer" className="w-full" value={fw.haPeerNodeId ?? ''} onChange={(e) => set({ haPeerNodeId: e.target.value || undefined })}>
            <option value="">{t('editor.firewall.haPeerNone')}</option>
            {firewallNodes.filter((f) => f.id !== node.id).map((f, i) => (
              <option key={f.id} value={f.id}>{f.label || t('editor.firewallLegend.unnamed', { index: i + 1 })}</option>
            ))}
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t('editor.firewall.interfaces')}</Label>
          <Button variant="secondary" className="px-2 py-1 text-xs" onClick={addIface}>{t('editor.firewall.addInterface')}</Button>
        </div>
        {fw.interfaces.map((iface, i) => (
          <div key={iface.id} className="space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700">
            <div className="flex items-center gap-1">
              <DebouncedTextInput className="flex-1" value={iface.name} placeholder={t('editor.firewall.ifaceName')} aria-label={t('editor.firewall.ifaceName')} onCommit={(v) => updateIface(i, { name: v })} />
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => removeIface(i)} aria-label={t('editor.firewall.remove')}>✕</Button>
            </div>
            <Select aria-label={t('editor.firewall.zone')} className="w-full" value={iface.zone ?? 'other'} onChange={(e) => updateIface(i, { zone: e.target.value as IfaceZone })}>
              {IFACE_ZONES.map((z) => (<option key={z} value={z}>{t(`editor.firewall.zoneOption.${z}`)}</option>))}
            </Select>
            <TextInput className="w-full font-mono" value={iface.ip ?? ''} placeholder={t('editor.firewall.ip')} aria-label={t('editor.firewall.ip')} onChange={(e) => updateIface(i, { ip: e.target.value || undefined })} />
            <TextInput className="w-full font-mono" value={iface.subnet ?? ''} placeholder={t('editor.firewall.subnet')} aria-label={t('editor.firewall.subnet')} onChange={(e) => updateIface(i, { subnet: e.target.value || undefined })} />
            <Select aria-label={t('editor.firewall.vlan')} className="w-full" value={iface.vlanRef ?? ''} onChange={(e) => updateIface(i, { vlanRef: e.target.value || undefined })}>
              <option value="">{t('editor.firewall.vlanNone')}</option>
              {vlans.map((v) => (<option key={v.id} value={v.id}>{`${v.vlanId} · ${v.name}`}</option>))}
            </Select>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t('editor.firewall.rules')}</Label>
          <Button variant="secondary" className="px-2 py-1 text-xs" onClick={addRule}>{t('editor.firewall.addRule')}</Button>
        </div>
        {fw.rules.map((rule, i) => (
          <div key={rule.id} className={cn('space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700', rule.enabled === false && 'opacity-60')}>
            <div className="flex items-center gap-1">
              <span className="flex-1 text-xs font-medium text-slate-500">{i + 1}</span>
              <Button variant="secondary" className="px-2 py-1 text-xs" disabled={i === 0} onClick={() => moveRule(i, -1)} aria-label={t('editor.firewall.moveUp')}>↑</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" disabled={i === fw.rules.length - 1} onClick={() => moveRule(i, 1)} aria-label={t('editor.firewall.moveDown')}>↓</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => removeRule(i)} aria-label={t('editor.firewall.remove')}>✕</Button>
            </div>
            <Select aria-label={t('editor.firewall.action')} className="w-full" value={rule.action} onChange={(e) => updateRule(i, { action: e.target.value as RuleAction })}>
              {RULE_ACTIONS.map((a) => (<option key={a} value={a}>{t(`editor.firewall.actionOption.${a}`)}</option>))}
            </Select>
            <TextInput className="w-full" value={rule.source ?? ''} placeholder={t('editor.firewall.source')} aria-label={t('editor.firewall.source')} onChange={(e) => updateRule(i, { source: e.target.value || undefined })} />
            <TextInput className="w-full" value={rule.destination ?? ''} placeholder={t('editor.firewall.destination')} aria-label={t('editor.firewall.destination')} onChange={(e) => updateRule(i, { destination: e.target.value || undefined })} />
            <TextInput className="w-full" value={rule.service ?? ''} placeholder={t('editor.firewall.service')} aria-label={t('editor.firewall.service')} onChange={(e) => updateRule(i, { service: e.target.value || undefined })} />
            <Select className="w-full" aria-label={t('editor.firewall.protocol')} value={rule.protocol ?? ''} onChange={(e) => updateRule(i, { protocol: (e.target.value || undefined) as RuleProtocol | undefined })}>
              <option value="">{t('editor.firewall.protocolAny')}</option>
              {RULE_PROTOCOLS.map((pr) => (<option key={pr} value={pr}>{pr}</option>))}
            </Select>
            <TextInput className="w-full" value={rule.port ?? ''} placeholder={t('editor.firewall.port')} aria-label={t('editor.firewall.port')} onChange={(e) => updateRule(i, { port: e.target.value || undefined })} />
            <Select aria-label={t('editor.firewall.ifaceRef')} className="w-full" value={rule.interfaceId ?? ''} onChange={(e) => updateRule(i, { interfaceId: e.target.value || undefined })}>
              <option value="">{t('editor.firewall.ifaceNone')}</option>
              {fw.interfaces.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
            </Select>
            <TextInput value={rule.description ?? ''} placeholder={t('editor.firewall.description')} aria-label={t('editor.firewall.description')} onChange={(e) => updateRule(i, { description: e.target.value || undefined })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={rule.enabled !== false} onChange={(e) => updateRule(i, { enabled: e.target.checked ? undefined : false })} />
              {t('editor.firewall.enabled')}
            </label>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t('editor.firewall.nat')}</Label>
          <Button variant="secondary" className="px-2 py-1 text-xs" onClick={addNat}>{t('editor.firewall.addNat')}</Button>
        </div>
        {fw.nat.map((nat, i) => (
          <div key={nat.id} className={cn('space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700', nat.enabled === false && 'opacity-60')}>
            <div className="flex items-center gap-1">
              <DebouncedTextInput className="flex-1" value={nat.name ?? ''} placeholder={t('editor.firewall.natName')} aria-label={t('editor.firewall.natName')} onCommit={(v) => updateNat(i, { name: v || undefined })} />
              <Button variant="secondary" className="px-2 py-1 text-xs" disabled={i === 0} onClick={() => moveNat(i, -1)} aria-label={t('editor.firewall.moveUp')}>↑</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" disabled={i === fw.nat.length - 1} onClick={() => moveNat(i, 1)} aria-label={t('editor.firewall.moveDown')}>↓</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => removeNat(i)} aria-label={t('editor.firewall.remove')}>✕</Button>
            </div>
            <Select className="w-full" aria-label={t('editor.firewall.protocol')} value={nat.protocol ?? ''} onChange={(e) => updateNat(i, { protocol: (e.target.value || undefined) as RuleProtocol | undefined })}>
              <option value="">{t('editor.firewall.protocolAny')}</option>
              {RULE_PROTOCOLS.map((pr) => (<option key={pr} value={pr}>{pr}</option>))}
            </Select>
            <Select aria-label={t('editor.firewall.ifaceRef')} className="w-full" value={nat.interfaceId ?? ''} onChange={(e) => updateNat(i, { interfaceId: e.target.value || undefined })}>
              <option value="">{t('editor.firewall.ifaceNone')}</option>
              {fw.interfaces.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
            </Select>
            <TextInput className="w-full" value={nat.externalPort ?? ''} placeholder={t('editor.firewall.extPort')} aria-label={t('editor.firewall.extPort')} onChange={(e) => updateNat(i, { externalPort: e.target.value || undefined })} />
            <TextInput className="w-full font-mono" value={nat.internalHost ?? ''} placeholder={t('editor.firewall.intHost')} aria-label={t('editor.firewall.intHost')} onChange={(e) => updateNat(i, { internalHost: e.target.value || undefined })} />
            <TextInput className="w-full" value={nat.internalPort ?? ''} placeholder={t('editor.firewall.intPort')} aria-label={t('editor.firewall.intPort')} onChange={(e) => updateNat(i, { internalPort: e.target.value || undefined })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={nat.enabled !== false} onChange={(e) => updateNat(i, { enabled: e.target.checked ? undefined : false })} />
              {t('editor.firewall.enabled')}
            </label>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t('editor.firewall.vpns')}</Label>
          <Button variant="secondary" className="px-2 py-1 text-xs" onClick={addVpn}>{t('editor.firewall.addVpn')}</Button>
        </div>
        {fw.vpns.map((vpn, i) => (
          <div key={vpn.id} className="space-y-1 rounded-md border border-slate-200 p-2 dark:border-slate-700">
            <div className="flex items-center gap-1">
              <DebouncedTextInput className="flex-1" value={vpn.name} placeholder={t('editor.firewall.vpnName')} aria-label={t('editor.firewall.vpnName')} onCommit={(v) => updateVpn(i, { name: v })} />
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => removeVpn(i)} aria-label={t('editor.firewall.remove')}>✕</Button>
            </div>
            <Select aria-label={t('editor.firewall.kind')} className="w-full" value={vpn.kind} onChange={(e) => updateVpn(i, { kind: e.target.value as VpnKind })}>
              {VPN_KINDS.map((k) => (<option key={k} value={k}>{t(`editor.firewall.kindOption.${k}`)}</option>))}
            </Select>
            <Select aria-label={t('editor.firewall.vpnProtocol')} className="w-full" value={vpn.protocol ?? ''} onChange={(e) => updateVpn(i, { protocol: (e.target.value || undefined) as VpnProtocol | undefined })}>
              <option value="">{t('editor.firewall.protocolAny')}</option>
              {VPN_PROTOCOLS.map((pr) => (<option key={pr} value={pr}>{t(`editor.firewall.vpnProtocolOption.${pr}`)}</option>))}
            </Select>
            <TextInput className="w-full" value={vpn.peer ?? ''} placeholder={t('editor.firewall.peer')} aria-label={t('editor.firewall.peer')} onChange={(e) => updateVpn(i, { peer: e.target.value || undefined })} />
            <TextInput className="w-full" value={vpn.localSubnets ?? ''} placeholder={t('editor.firewall.localSubnets')} aria-label={t('editor.firewall.localSubnets')} onChange={(e) => updateVpn(i, { localSubnets: e.target.value || undefined })} />
            <TextInput className="w-full" value={vpn.remoteSubnets ?? ''} placeholder={t('editor.firewall.remoteSubnets')} aria-label={t('editor.firewall.remoteSubnets')} onChange={(e) => updateVpn(i, { remoteSubnets: e.target.value || undefined })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={vpn.active ?? false} onChange={(e) => updateVpn(i, { active: e.target.checked })} />
              {t('editor.firewall.active')}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Eigenschaften der Switch-Legende (C5.3): Quell-Switch-Auswahl + „Alle Ports"-Schalter. */
function SwitchLegendFields({
  node,
  switchNodes,
  onChangeNode,
}: {
  node: SceneNode;
  switchNodes: { id: string; label: string }[];
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  const legend = node.switchLegend ?? {};
  const set = (patch: Partial<SwitchLegendData>) => onChangeNode({ switchLegend: { ...legend, ...patch } });
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="sl-source">{t('editor.switchLegend.source')}</Label>
        <Select
          id="sl-source"
          className="w-full"
          value={legend.sourceNodeId ?? ''}
          onChange={(e) => set({ sourceNodeId: e.target.value || undefined })}
        >
          <option value="">{t('editor.switchLegend.sourceNone')}</option>
          {switchNodes.map((s, i) => (
            <option key={s.id} value={s.id}>
              {s.label || t('editor.switchLegend.unnamed', { index: i + 1 })}
            </option>
          ))}
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={legend.showAllPorts ?? false}
          onChange={(e) => set({ showAllPorts: e.target.checked })}
        />
        {t('editor.switchLegend.showAll')}
      </label>
    </div>
  );
}

function FirewallLegendFields({
  node,
  firewallNodes,
  onChangeNode,
}: {
  node: SceneNode;
  firewallNodes: { id: string; label: string }[];
  onChangeNode: (patch: Partial<SceneNode>) => void;
}) {
  const { t } = useTranslation();
  const legend = node.firewallLegend ?? {};
  const set = (patch: Partial<FirewallLegendData>) => onChangeNode({ firewallLegend: { ...legend, ...patch } });
  const sections = legend.sections ?? ['rules'];
  const toggleSection = (s: 'rules' | 'nat' | 'vpns', on: boolean) => {
    const next = on ? [...new Set([...sections, s])] : sections.filter((x) => x !== s);
    set({ sections: next });
  };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div>
        <Label htmlFor="fl-source">{t('editor.firewallLegend.source')}</Label>
        <Select
          id="fl-source"
          className="w-full"
          value={legend.sourceNodeId ?? ''}
          onChange={(e) => set({ sourceNodeId: e.target.value || undefined })}
        >
          <option value="">{t('editor.firewallLegend.sourceNone')}</option>
          {firewallNodes.map((f, i) => (
            <option key={f.id} value={f.id}>
              {f.label || t('editor.firewallLegend.unnamed', { index: i + 1 })}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label>{t('editor.firewallLegend.sections')}</Label>
        <div className="space-y-1">
          {(['rules', 'nat', 'vpns'] as const).map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sections.includes(s)} onChange={(e) => toggleSection(s, e.target.checked)} />
              {t(`editor.firewallLegend.${s}`)}
            </label>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={legend.showDisabled ?? false} onChange={(e) => set({ showDisabled: e.target.checked })} />
        {t('editor.firewallLegend.showDisabled')}
      </label>
    </div>
  );
}

export type ReorderAction = 'front' | 'forward' | 'backward' | 'back';

interface PropertyPanelProps {
  node: SceneNode | null;
  nodes: SceneNode[];
  vlans: VlanDef[];
  users: UserDef[];
  switchNodes: { id: string; label: string }[];
  firewallNodes: { id: string; label: string }[];
  connector: Connector | null;
  selectionCount: number;
  canGroup: boolean;
  canUngroup: boolean;
  canvasFormat: CanvasFormat;
  onChangeCanvasFormat: (format: CanvasFormat) => void;
  onChangeNode: (patch: Partial<SceneNode>) => void;
  /** Patch auf alle ausgewählten Knoten anwenden (Multi-Edit, z. B. Farbe). */
  onChangeSelection: (patch: Partial<SceneNode>) => void;
  portSelection: { node: SceneNode; portIndex: number } | null;
  onChangePort: (patch: Partial<PortDef>) => void;
  onPickDeviceImage: () => void;
  onChangeConnector: (patch: Partial<Connector>) => void;
  onReorder: (action: ReorderAction) => void;
  onGroup: () => void;
  onUngroup: () => void;
  onDelete: () => void;
}

export function PropertyPanel({
  node,
  nodes,
  vlans,
  users,
  switchNodes,
  firewallNodes,
  connector,
  selectionCount,
  canGroup,
  canUngroup,
  canvasFormat,
  onChangeCanvasFormat,
  onChangeNode,
  onChangeSelection,
  portSelection,
  onChangePort,
  onPickDeviceImage,
  onChangeConnector,
  onReorder,
  onGroup,
  onUngroup,
  onDelete,
}: PropertyPanelProps) {
  const { t } = useTranslation();
  // Connector-Eigenschaften in „Basis" vs. „Erweitert" gliedern (viele Felder).
  const [connectorTab, setConnectorTab] = useState<'basic' | 'advanced'>('basic');

  // Einzeln angewaehlter Switch-Port: nur das Port-Detail anzeigen.
  if (portSelection) {
    return (
      <aside className="h-full w-full space-y-4 overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-700">
        <PortFields
          node={portSelection.node}
          portIndex={portSelection.portIndex}
          vlans={vlans}
          onChange={onChangePort}
        />
      </aside>
    );
  }

  // Mehrfachauswahl: Gruppieren/Aufheben/Löschen + Ebenen.
  if (selectionCount > 1) {
    return (
      <aside className="h-full w-full space-y-4 overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-700">
        <p className="text-sm font-medium">{t('editor.multiSelection', { count: selectionCount })}</p>
        <div className="space-y-2">
          <Button variant="secondary" className="w-full" disabled={!canGroup} onClick={onGroup}>
            {t('editor.group')}
          </Button>
          <Button variant="secondary" className="w-full" disabled={!canUngroup} onClick={onUngroup}>
            {t('editor.ungroup')}
          </Button>
        </div>
        <div>
          <Label>{t('editor.layer')}</Label>
          <div className="grid grid-cols-2 gap-1">
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => onReorder('front')}>
              {t('editor.layerFront')}
            </Button>
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => onReorder('forward')}>
              {t('editor.layerForward')}
            </Button>
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => onReorder('backward')}>
              {t('editor.layerBackward')}
            </Button>
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => onReorder('back')}>
              {t('editor.layerBack')}
            </Button>
          </div>
        </div>
        {/* Multi-Edit: gemeinsame Farben auf alle ausgewählten Knoten anwenden. */}
        <div>
          <Label>{t('editor.color')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">{t('editor.fillColor')}</span>
              <input
                type="color"
                aria-label={t('editor.fillColor')}
                className="h-9 w-full cursor-pointer rounded-md border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
                defaultValue="#e2e8f0"
                onChange={(e) => onChangeSelection({ fill: e.target.value })}
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">{t('editor.strokeColor')}</span>
              <input
                type="color"
                aria-label={t('editor.strokeColor')}
                className="h-9 w-full cursor-pointer rounded-md border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
                defaultValue="#475569"
                onChange={(e) => onChangeSelection({ stroke: e.target.value })}
              />
            </div>
          </div>
        </div>
        <Button variant="danger" className="w-full" onClick={onDelete}>
          {t('editor.delete')}
        </Button>
      </aside>
    );
  }

  if (!node && !connector) {
    return (
      <aside className="h-full w-full space-y-4 overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-700">
        <div>
          <Label htmlFor="doc-format">{t('editor.canvasFormat')}</Label>
          <Select
            id="doc-format"
            className="w-full"
            value={canvasFormat}
            onChange={(e) => onChangeCanvasFormat(e.target.value as CanvasFormat)}
          >
            {CANVAS_FORMATS.map((f) => (
              <option key={f} value={f}>
                {t(`editor.format.${f}`)}
              </option>
            ))}
          </Select>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('editor.selectHint')}</p>
      </aside>
    );
  }

  return (
    <aside className="h-full w-full space-y-4 overflow-y-auto border-l border-slate-200 p-4 dark:border-slate-700">
      {node && (
        <>
          <div>
            <Label htmlFor="p-label">{t('editor.label')}</Label>
            <DebouncedTextarea
              id="p-label"
              rows={3}
              value={node.label}
              onCommit={(v) => onChangeNode({ label: v })}
            />
          </div>
          {node.device && (
            <DeviceFields
              device={node.device}
              vlans={vlans}
              users={users}
              onChange={(next) => onChangeNode({ device: next })}
              onPickImage={onPickDeviceImage}
            />
          )}
          {node.switch && <SwitchFields node={node} onChangeNode={onChangeNode} />}
          {node.host && (
            <HostFields node={node} vlans={vlans} volumes={sceneVolumes(nodes)} onChangeNode={onChangeNode} />
          )}
          {node.clusterStorage && <ClusterStorageFields node={node} onChangeNode={onChangeNode} />}
          {node.firewall && (
            <FirewallFields node={node} vlans={vlans} firewallNodes={firewallNodes} onChangeNode={onChangeNode} />
          )}
          {node.type === 'switch-legend' && (
            <SwitchLegendFields node={node} switchNodes={switchNodes} onChangeNode={onChangeNode} />
          )}
          {node.type === 'firewall-legend' && (
            <FirewallLegendFields node={node} firewallNodes={firewallNodes} onChangeNode={onChangeNode} />
          )}
          <div>
            <Label htmlFor="p-env">{t('editor.environment')}</Label>
            <Select
              id="p-env"
              className="w-full"
              value={node.environmentType}
              onChange={(e) => {
                const env = e.target.value as EnvironmentType;
                const patch: Partial<SceneNode> = { environmentType: env };
                // Farben mitführen (außer bei Textboxen, die weiß bleiben).
                if (node.type !== 'text') {
                  patch.fill = ENV_COLORS[env].fill;
                  patch.stroke = ENV_COLORS[env].stroke;
                }
                onChangeNode(patch);
              }}
            >
              {ENVIRONMENT_TYPES.map((env) => (
                <option key={env} value={env}>
                  {t(`environment.${env}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="p-fontsize">{t('editor.fontSize')}</Label>
            <Select
              id="p-fontsize"
              className="w-full"
              value={node.fontSize ?? 14}
              onChange={(e) => onChangeNode({ fontSize: Number(e.target.value) })}
            >
              {FONT_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          {supportsLabelPosition(node.type) && (
            <div>
              <Label>{t('editor.labelPosition')}</Label>
              <LabelPositionGrid
                value={effectiveLabelPosition(node)}
                onChange={(labelPosition) => onChangeNode({ labelPosition })}
              />
            </div>
          )}
          <div>
            <Label>{t('editor.layer')}</Label>
            <div className="grid grid-cols-2 gap-1">
              <Button variant="secondary" className="px-2 py-1 text-xs" title={t('editor.layerFront')} onClick={() => onReorder('front')}>
                {t('editor.layerFront')}
              </Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" title={t('editor.layerForward')} onClick={() => onReorder('forward')}>
                {t('editor.layerForward')}
              </Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" title={t('editor.layerBackward')} onClick={() => onReorder('backward')}>
                {t('editor.layerBackward')}
              </Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" title={t('editor.layerBack')} onClick={() => onReorder('back')}>
                {t('editor.layerBack')}
              </Button>
            </div>
          </div>
        </>
      )}

      {connector && (
        <>
          {/* Tabs: Basis vs. Erweitert (gliedert die 10+ Connector-Felder) */}
          <div role="tablist" className="flex gap-1 rounded-md bg-slate-100 p-1 dark:bg-slate-800">
            <button
              type="button"
              role="tab"
              aria-selected={connectorTab === 'basic'}
              onClick={() => setConnectorTab('basic')}
              className={cn(
                'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                connectorTab === 'basic'
                  ? 'bg-white shadow-sm dark:bg-slate-700'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200',
              )}
            >
              {t('editor.tabBasic')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={connectorTab === 'advanced'}
              onClick={() => setConnectorTab('advanced')}
              className={cn(
                'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                connectorTab === 'advanced'
                  ? 'bg-white shadow-sm dark:bg-slate-700'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200',
              )}
            >
              {t('editor.tabAdvanced')}
            </button>
          </div>

          {connectorTab === 'basic' && (
            <>
              <div>
                <Label htmlFor="c-label">{t('editor.labelAbove')}</Label>
                <DebouncedTextInput
                  id="c-label"
                  value={connector.label ?? ''}
                  onCommit={(v) => onChangeConnector({ label: v })}
                />
              </div>
              <div>
                <Label htmlFor="c-label-below">{t('editor.labelBelow')}</Label>
                <DebouncedTextInput
                  id="c-label-below"
                  value={connector.labelBelow ?? ''}
                  onCommit={(v) => onChangeConnector({ labelBelow: v })}
                />
              </div>
              <div>
                <Label htmlFor="c-arrows">{t('editor.arrows')}</Label>
                <Select
                  id="c-arrows"
                  className="w-full"
                  value={arrowModeOf(connector)}
                  onChange={(e) => onChangeConnector(arrowModePatch(e.target.value as ArrowMode))}
                >
                  {ARROW_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`editor.arrow.${mode}`)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="c-style">{t('editor.lineStyle')}</Label>
                <Select
                  id="c-style"
                  className="w-full"
                  value={connectorLineStyle(connector)}
                  onChange={(e) => onChangeConnector({ lineStyle: e.target.value as LineStyle })}
                >
                  {LINE_STYLES.map((s) => (
                    <option key={s} value={s}>
                      {t(`editor.lineStyleOption.${s}`)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="c-width">{t('editor.strokeWidth')}</Label>
                <Select
                  id="c-width"
                  className="w-full"
                  value={connector.strokeWidth ?? 2}
                  onChange={(e) => onChangeConnector({ strokeWidth: Number(e.target.value) })}
                >
                  {STROKE_WIDTHS.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="c-color">{t('editor.color')}</Label>
                <input
                  id="c-color"
                  type="color"
                  className="h-9 w-full cursor-pointer rounded-md border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
                  value={connector.stroke ?? '#475569'}
                  onChange={(e) => onChangeConnector({ stroke: e.target.value })}
                />
              </div>
            </>
          )}

          {connectorTab === 'advanced' && (
            <>
              <div>
                <Label htmlFor="c-routing">{t('editor.routing')}</Label>
                <Select
                  id="c-routing"
                  className="w-full"
                  value={connector.routing ?? 'straight'}
                  onChange={(e) => onChangeConnector({ routing: e.target.value as ConnectorRouting })}
                >
                  {CONNECTOR_ROUTINGS.map((r) => (
                    <option key={r} value={r}>
                      {t(`editor.routingOption.${r}`)}
                    </option>
                  ))}
                </Select>
              </div>
              {(connector.routing ?? 'straight') === 'orthogonal' && connector.bendRatio !== undefined && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => onChangeConnector({ bendRatio: undefined })}
                >
                  {t('editor.bendReset')}
                </Button>
              )}
              <div>
                <Label htmlFor="c-bridge">{t('editor.connector.bridge')}</Label>
                <Select
                  id="c-bridge"
                  className="w-full"
                  value={connector.bridge ?? 'default'}
                  onChange={(e) => onChangeConnector({ bridge: e.target.value as BridgeMode })}
                >
                  {BRIDGE_MODES.map((b) => (
                    <option key={b} value={b}>
                      {t(`editor.connector.bridgeMode.${b}`)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="c-cable">{t('editor.cable.type')}</Label>
                <Select
                  id="c-cable"
                  className="w-full"
                  value={connector.cableType ?? ''}
                  onChange={(e) => onChangeConnector({ cableType: (e.target.value || undefined) as CableType | undefined })}
                >
                  <option value="">{t('editor.cable.typeNone')}</option>
                  {CABLE_TYPES.map((c) => (
                    <option key={c} value={c}>
                      {t(`editor.cable.typeOption.${c}`)}
                    </option>
                  ))}
                </Select>
              </div>
              {connector.cableType === 'fiber' && (
                <>
                  <div>
                    <Label htmlFor="c-fiber">{t('editor.cable.fiberMode')}</Label>
                    <Select
                      id="c-fiber"
                      className="w-full"
                      value={connector.fiberMode ?? 'mm'}
                      onChange={(e) => onChangeConnector({ fiberMode: e.target.value as FiberMode })}
                    >
                      {FIBER_MODES.map((m) => (
                        <option key={m} value={m}>
                          {t(`editor.cable.fiberOption.${m}`)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="c-speed">{t('editor.cable.speed')}</Label>
                    <Select
                      id="c-speed"
                      className="w-full"
                      value={connector.cableSpeed ?? ''}
                      onChange={(e) => onChangeConnector({ cableSpeed: (e.target.value || undefined) as PortSpeed | undefined })}
                    >
                      <option value="">{t('editor.switch.speedNone')}</option>
                      {PORT_SPEEDS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      <Button variant="danger" className="w-full" onClick={onDelete}>
        {t('editor.delete')}
      </Button>
    </aside>
  );
}
